<ng-template #voidDocumentsButton>
	<eif-custom-button
		[text]="'search-docs.deleteDocuments' | translate"
		[preIcon]="'fa-regular fa-minus-circle'"
		(action)="confirmDocumentsDeletions()">
	</eif-custom-button>
</ng-template>

<ng-template
	#documentCard
	let-row>
	<div class="eif-card">
		<div class="eif-card-header">
			<div class="is-bold truncate">{{ row.Number }}</div>
			<div>{{ row.Type | translate: 'list-docType' }}</div>
		</div>
		<div class="eif-card-section">
			<div class="label">{{ 'search-docs.supplier' | translate }}</div>
			<div>{{ row.Supplier?.Name }}</div>
			<div>{{ row.Supplier?.CountryCode }}{{ row.Supplier?.VatNumber }}</div>
		</div>
		<div class="eif-card-section">
			<div class="label">{{ 'search-docs.buyer' | translate }}</div>
			<div>{{ row.Buyer?.Name }}</div>
			<div>{{ row.Buyer?.CountryCode }}{{ row.Buyer?.VatNumber }}</div>
		</div>
		<div class="eif-card-section">
			<div class="label">{{ 'search-docs.docAmount' | translate }}</div>
			<div class="is-bold">{{ row.TotalAmount | currencyLocale: row.Currency }}</div>
		</div>
		<div class="eif-card-section">
			<div class="label">{{ 'search-docs.creationDate' | translate }}</div>
			<div>{{ row.CreationDate | dateLocale: 'yyyy/MM/dd HH:mm' }}</div>
		</div>
		<div class="eif-card-section">
			<div class="label d-flex align-items-center">
				{{ 'search-docs.confirmedPaymentDate' | translate }}
				<div
					[eifTooltip]="'search-docs.confirmedPaymentDate.tooltip' | translate"
					class="is-color-danger">
					<i class="fa-solid fa-exclamation-circle ms-1"></i>
				</div>
			</div>
			<div>{{ row.BuyerPaymentDate | dateLocale: 'yyyy/MM/dd' }}</div>
		</div>
		<div class="eif-card-section">
			<div class="label">{{ 'search-docs.approvalState' | translate }}</div>
			<div>
				<span class="tag {{ row.ApprovalState }}">{{ row.ApprovalState | translate: 'list-approvalState' }}</span>
			</div>
		</div>
		<div class="eif-card-section">
			<div class="label">{{ 'search-docs.paymentState' | translate }}</div>
			<div>
				<span class="tag is-payment-state-{{ row.PaymentState }}">{{ row.PaymentState | translate: 'list-paymentState' }}</span>
			</div>
		</div>
	</div>
</ng-template>

<div class="container-fluid animated fadeIn">
	<div class="page-header-wrapper">
		<div class="page-header">
			<div class="page-header-left">
				<h2>
					{{ 'search-docs.pageTitle' | translate }}
				</h2>
			</div>
			<div class="page-header-right">
				<!-- button: export financial movements -->
				@if (canSeeExportFinancialMovementsOptions()) {
					<eif-custom-button
						[text]="'search-docs.exportFinancialMovements'"
						(action)="showModal($event)">
					</eif-custom-button>
				}

				<!-- button: upload documents -->
				@if (canSeeUploadDocumentsButton()) {
					<eif-custom-menu
						[text]="'search-docs.documentRecordsUpload'"
						[postIcon]="'fa-solid fa-caret-down'"
						[menuItems]="uploadDocumentsMenuItems">
					</eif-custom-menu>
				}

				<!-- button: upload documents (Confirming only) -->
				@if (canSeeDocumentRecordsUploadConfirmingButton()) {
					<eif-custom-button
						[text]="'search-docs.documentRecordsUpload'"
						(action)="gotoDocumentsUploadConfirming()">
					</eif-custom-button>
				}

				<!-- button: upload documents (Factoring only) -->
				@if (canSeeDocumentRecordsUploadFactoringButton()) {
					<eif-custom-button
						[text]="'search-docs.documentRecordsUpload'"
						(action)="gotoDocumentsUploadFactoring()">
					</eif-custom-button>
				}

				<!-- button: request funding -->
				@if (canSeeRequestFundingButton()) {
					<eif-custom-menu
						[text]="'search-docs.requestNewFunding'"
						[postIcon]="'fa-solid fa-caret-down'"
						[menuItems]="requestFundingMenuItems">
					</eif-custom-menu>
				}

				<!-- button: request funding (Confirming only) -->
				@if (canSeeConfirmingOnlyRequestFundingButton()) {
					<eif-custom-button
						[text]="'search-docs.newFundingRequestStandalone'"
						(action)="gotoConfirmingFundingRequestCreate()">
					</eif-custom-button>
				}

				<!-- button: request advance payment (Factoring only) -->
				@if (canSeeFactoringOnlyRequestAdvancePaymentButton()) {
					<eif-custom-button
						[text]="'search-docs.newAdvanceRequestStandalone'"
						(action)="gotoFactoringAdvancePaymentCreate()">
					</eif-custom-button>
				}
			</div>
		</div>
	</div>

	<!-- CONTENT -->
	<div class="simple-card top-line ps-3 pe-3">
		<eif-custom-table
			#customTable
			[columns]="tableColumns"
			[rows]="documents"
			[loading]="isTableLoading"
			[pagination]="tablePagination"
			[filters]="tableFilters"
			[enableFilters]="true"
			[identifier]="'documents-search'"
			[pathPattern]="'/documents'"
			[selectActions]="selectActions"
			[userTimeZone]="loggedUser?.TimeZone || 'UTC'"
			(searchRequested)="onSearchRequested($event)"
			[selectionMode]="'multiple'"
			[persistSelection]="true"
			(selectionChange)="onSelectionChange($event)"
			[cardTemplate]="documentCard"
			[export]="tableExport"
			[getRowId]="getRowId">
		</eif-custom-table>
	</div>
</div>

@if (isModalVisible) {
	<div class="modal-financial-movements animated fadeIn">
		<div
			class="modal-financial-movements-content"
			(clickOutside)="closeModal()">
			<div class="d-flex align-items-center justify-content-between gap-2 is-tooltip-label modal-financial-movements-header-wrapper">
				<h2 class="modal-financial-movements-header">{{ 'search-docs.exportFinancialMovements' | translate }}</h2>
			</div>
			<div class="modal-wrapper">
				<eif-contracts-in-company
					[uploadContractType]="uploadContractType"
					[initialCompanyId]="companyId"
					[initialContractId]="fileRelatedContractId"
					(companyChange)="onCompanyChange($event)"
					(contractChange)="onContractChange($event)"
					(validationChange)="onValidationChange($event)">
				</eif-contracts-in-company>
				<div>
					<p class="section-label">
						{{ 'search-docs.exportFinancialMovementsRangeDateTitle' | translate }}
					</p>
					<div class="section-wrapper is-secondary-section">
						<eif-custom-date-picker
							[label]="'search-docs.exportFinancialMovementsRangeDate' | translate"
							[fullWidth]="true"
							[placeholder]="'search-docs.exportFinancialMovementsRangeDatePlaceholder' | translate"
							[name]="'date-range-picker-modal'"
							[isRange]="true"
							[maxRange]="30"
							[validationRules]="getFinancialMovementsDateRangeValidationRules()"
							(selectionChange)="onModalRangeChange($event)"
							(validationChange)="onDateRangeValidationChange($event)">
						</eif-custom-date-picker>
					</div>
				</div>
			</div>
			<div class="is-actions-container">
				<eif-custom-button
					[text]="'button.cancel' | translate"
					[color]="'neutral'"
					[variant]="'outlined'"
					[preIcon]="'fa-solid fa-times'"
					(action)="closeModal()">
				</eif-custom-button>
				<eif-custom-button
					[text]="'button.export' | translate"
					[preIcon]="'fa-solid fa-check'"
					[loading]="isExportingRequest"
					[disabled]="isExportFinancialMovementsDisabled()"
					(action)="exportFinancialMovements()">
				</eif-custom-button>
			</div>
		</div>
	</div>
}
